import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Provider as ReduxProvider } from 'react-redux';
import { createMemoryHistory } from 'history';
import {
  createHttpClient,
  withTranslation,
  withWixSdk,
} from '@wix/yoshi-flow-editor';
import {
  consumeEvents,
  createFunctionProxy,
  createWorkerHandlerMiddleware,
} from './lib';
import { WidgetPropsProvider } from '../containers/widget-props';
import {
  setInitialAppSettings,
  updateAppSettings,
} from '../redux/actions/app-settings';
import { setInstance } from '../configure-client';
import { setHydratedData } from '../redux/hydrated-data/hydrated-data';
import { logBi } from './actions/bi';
import { experimentsStore } from '@wix/wix-vod-shared/common';
import { withStyleParams } from '@wix/wix-vod-shared/components';
import { initPublicServices } from '../api/public';
import { initWixSDK } from '../utils/wix-sdk';
import { StyleParamsCSSVariables } from '../components/style-params-css-variables/StyleParamsCSSVariables';
import { initTranslation } from '../utils/translation';
import { updateControllerState } from '../redux/actions/controller-state';
import { ControllerStateContext } from '../containers/withHandlersAndState';
import { initPrivateServices } from '../api/private';

const withErrorBoundary = (Comp) => {
  return class BoundaryWrapComponent extends React.Component {
    state = { error: false };
    componentDidCatch(err, errorInfo) {
      this.props.captureException(err, { extra: { reactInfo: errorInfo } });
      this.setState({ error: true });
    }
    render() {
      if (this.state.error) {
        return null;
      }
      return <Comp {...this.props} />;
    }
  };
};

export function widgetWrapper({ createStore, Component }) {
  class StoreWrapper extends React.Component {
    static propTypes = {
      hydratedSource: PropTypes.object,
      pubSubEvents: PropTypes.object,
      style: PropTypes.object,
      t: PropTypes.func.isRequired,
      configData: PropTypes.shape({
        baseUrl: PropTypes.string,
        instance: PropTypes.string,
      }),
    };

    constructor(props) {
      super(props);

      const {
        configData: { instance, v3ToV2MappingBaseUrl },
        experiments,
        wixSDK: { Wix },
        isSSR,
        t,
        handlers,
        controllerState,
        captureException,
      } = props;
      const httpClient = createHttpClient({
        isSSR,
        getAppToken: () => this.props.configData.instance,
      });

      initTranslation(t);
      initWixSDK(Wix);
      experimentsStore.set(experiments);

      initPrivateServices(httpClient, v3ToV2MappingBaseUrl);
      initPublicServices(httpClient, instance, v3ToV2MappingBaseUrl);

      const functionsProxy = (this.functionsProxy = createFunctionProxy());

      this.resolve = _.noop;

      if (isSSR) {
        this.promise = new Promise((_resolve) => {
          this.resolve = _resolve;
        });
      }

      const dispatch = (action) =>
        props.dispatchEv(functionsProxy.serializeAction(action));

      const dispatchOnPageReady = async (action) => {
        await this.promise;
        return dispatch(action);
      };

      const middlewares = [
        createWorkerHandlerMiddleware(isSSR ? dispatchOnPageReady : dispatch),
      ];

      const { configData, appState, styleParams } = this.props;

      this.history = createMemoryHistory({ initialEntries: [configData.url] });

      this.store = createStore({
        middlewares,
        initialState: appState,
        history: this.history,
        handlers,
        httpClient,
        reportError: captureException,
      });

      if (controllerState) {
        this.store.dispatch(updateControllerState(controllerState));
      }

      if (isSSR) {
        this.promise.then(this.logWidgetLoaded);
      } else {
        this.logWidgetLoaded();
      }

      this.updateInstance();

      this.store.dispatch(setInitialAppSettings(styleParams));
    }

    updateInstance() {
      setInstance(this.props.configData.instance);
    }

    componentDidUpdate(prevProps) {
      const { controllerState, styleParams } = this.props;

      if (
        prevProps.renderingEnv === 'backend' &&
        this.props.renderingEnv === 'browser'
      ) {
        this.resolve();
      }

      if (!_.isEqual(prevProps.configData, this.props.configData)) {
        this.updateInstance();
        this.store.dispatch(
          setHydratedData({
            instance: this.props.configData.instance,
          }),
        );
      }

      if (prevProps.controllerState !== controllerState) {
        this.store.dispatch(updateControllerState(controllerState));
      }

      if (prevProps.styleParams !== styleParams) {
        this.store.dispatch(updateAppSettings(styleParams));
      }
    }

    logWidgetLoaded = () => {
      this.store.dispatch(
        logBi('widget.loaded', {
          channelID: '00000000-0000-0000-0000-000000000000',
        }),
      );
    };

    UNSAFE_componentWillReceiveProps(newProps) {
      consumeEvents(newProps, (event) => {
        this.functionsProxy.callFunction(event);
      });
    }

    render() {
      const { store } = this;
      const { controllerState, handlers } = this.props;

      return (
        <ReduxProvider store={store}>
          <WidgetPropsProvider value={this.props}>
            <ControllerStateContext.Provider
              value={{
                handlers,
                controllerState,
              }}
            >
              <StyleParamsCSSVariables>
                <Component />
              </StyleParamsCSSVariables>
            </ControllerStateContext.Provider>
          </WidgetPropsProvider>
        </ReduxProvider>
      );
    }
  }

  return withErrorBoundary(
    withStyleParams(withTranslation()(withWixSdk(StoreWrapper))),
  );
}
